import { useAppDispatch } from "app/hooks";
import { Button, InputWithButton, Sort, Search } from "components";
import { END_USER_SORTS, EndUserSortItem } from "helpers"; // Use new types and constants
import { useUserManagementContext } from "pages/UserManagement";
import { ChangeEvent, useEffect, useState } from "react";
import { Order } from "helpers";
import { t } from "i18next";
import { useTranslation } from "react-i18next";

export const UserHeader = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { onSearch, onSort, params } = useUserManagementContext();
  const [inputValue, setInputValue] = useState(params.search || "");
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1536);

  const translatedEndUserSorts = END_USER_SORTS.map((sort) => ({
    ...sort,
    name: t(sort.name),
  }));

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1536);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setInputValue(params.search || "");
  }, [params.search]);

  const mapSortItemToParams = (sortItem: EndUserSortItem) => {
    const [field, direction] = sortItem.split("-") as [string, "asc" | "desc"];
    const sortMap: Record<string, "full_name" | "city" | "email" | "phone_number" | "last_sign_in"> = {
      "full-name": "full_name",
      city: "city",
      email: "email",
      "phone-number": "phone_number",
      "last-sign-in": "last_sign_in",
    };
    const sort = sortMap[field];
    const order: Order = direction === "asc" ? "ASC" : "DESC";
    return { sort, order };
  };

  // Determine the selected sort for the Sort component
  const selectedSort: EndUserSortItem | undefined = END_USER_SORTS.find(
    (sort) =>
      sort.id === `${params.sort}-${params.order?.toLowerCase()}` ||
      (sort.id.includes(params.sort || "") && sort.id.endsWith(params.order?.toLowerCase() || ""))
  )?.id;

  return (
    <>
      <div className="flex flex-col gap-3 md:flex-row md:items-center xl:flex-row 2xl:justify-evenly">
        {isSmallScreen ? (
          <div className="w-full">
            <div className="w-6/2 mb-2 flex items-center gap-3">
              <InputWithButton
                inputProps={{
                  placeholder: "Search End Users",
                  variant: "modal",
                  value: inputValue,
                  onChange: (event: ChangeEvent<HTMLInputElement>) => setInputValue(event.target.value),
                  className: "border-primary focus:border-primary flex-1",
                }}
                buttonProps={{
                  children: <Search className="w-10" />,
                  className: "px-6",
                  onClick: () => onSearch(inputValue),
                }}
              />
            </div>
            <Sort
              label={t("userManagement.sortBy")}
              values={translatedEndUserSorts}
              onSort={(sort) => {
                const { sort: sortField, order } = mapSortItemToParams(sort as EndUserSortItem);
                onSort(sortField, order);
              }}
              selectedSort={selectedSort}
            />
          </div>
        ) : (
          <>
            <div className="flex w-full flex-col gap-3 xl:flex-row 2xl:w-auto 2xl:flex-row">
              <div className="w-6/12">
                <InputWithButton
                  inputProps={{
                    placeholder: t("userManagement.searchUsers"),
                    variant: "modal",
                    value: inputValue,
                    onChange: (event: ChangeEvent<HTMLInputElement>) => setInputValue(event.target.value),
                    className: "border-primary focus:border-primary",
                  }}
                  buttonProps={{
                    children: <Search className="w-10" />,
                    className: "px-6",
                    onClick: () => onSearch(inputValue),
                  }}
                />
              </div>
              <Sort
                label={t("userManagement.sortBy")}
                values={translatedEndUserSorts}
                onSort={(sort) => {
                  const { sort: sortField, order } = mapSortItemToParams(sort as EndUserSortItem);
                  onSort(sortField, order);
                }}
                selectedSort={selectedSort}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};
